import { Badge, Link, StatusBadge } from '@apollo/orbit';
import { capitalize } from 'lodash';
import React from 'react';

import {
  getAdmonitionProps,
  getBadgeProps,
} from 'src/app/graph/variantsHomepage/cloudRouterDetails/helpers';
import {
  CloudConstants,
  CloudProvider,
  CloudTier,
  Maybe,
  RouterStatus,
  ShardStatus,
} from 'src/lib/graphqlTypes/types';

import { GraphRef } from '../../hooks/useGraphRef';
import { cloudSettingsRouteConfig } from '../../routes';

import { CloudRouterDetailsAdmonition } from './CloudRouterDetailsAdmonition';

export type CloudRouterDetailsProps = {
  graphRef: GraphRef;
  router?: Maybe<{
    status: RouterStatus;
    constants: Pick<CloudConstants, 'durationBeforeDeleteSecs'>;
    nextStatus?: Maybe<RouterStatus>;
    routerVersion?: Maybe<{
      version: string;
      status: string;
    }>;
    shard?: Maybe<{
      status: ShardStatus;
      tier: CloudTier;
      region?: Maybe<{
        code: string;
        country: string;
        name: string;
        provider: CloudProvider;
      }>;
    }>;
  }>;
};

export const CloudRouterDetails = ({
  graphRef,
  router,
}: CloudRouterDetailsProps) => {
  if (!router || !graphRef) return <div>No router data available</div>;

  const {
    constants,
    status: routerStatus,
    nextStatus,
    routerVersion,
    shard,
  } = router;

  const version = routerVersion?.version ?? undefined;
  const versionStatus = routerVersion?.status ?? undefined;
  const shardStatus = shard?.status ?? undefined;
  const provider = shard?.region?.provider ?? undefined;
  const { code, country, name } = shard?.region ?? {
    code: undefined,
    country: undefined,
    name: undefined,
  };
  const { durationBeforeDeleteSecs } = constants ?? undefined;

  const badgeProps = getBadgeProps(
    routerStatus,
    nextStatus,
    shardStatus,
    provider,
  );
  const admonitionProps = getAdmonitionProps(
    routerStatus,
    shardStatus,
    provider,
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-baseline justify-between border-b border-primary">
        <p className="text-heading-xl">Cloud Router Details</p>
        <Link
          to={cloudSettingsRouteConfig.path({
            ...graphRef,
            tab: 'configuration',
          })}
        >
          Configure
        </Link>
      </div>
      <div className="flex items-center">
        <p className="text-heading-lg pr-2 text-primary">Status:</p>
        {badgeProps && (
          <StatusBadge
            variant="rounded"
            data-testid="status-badge"
            colorScheme={badgeProps.colorScheme}
          >
            {capitalize(badgeProps.text.toLowerCase())}
          </StatusBadge>
        )}
      </div>

      {admonitionProps && (
        <CloudRouterDetailsAdmonition
          status={routerStatus}
          nextStatus={nextStatus}
          shardStatus={shardStatus}
          graphRef={graphRef}
          durationBeforeDeleteSecs={durationBeforeDeleteSecs}
          provider={provider}
        />
      )}

      {version && (
        <div className="flex items-baseline gap-1">
          <p className="text-heading-lg mr-1 gap-1 text-primary">
            Version: <span className="text-body">{version}</span>
          </p>
          <Badge variant="info">{versionStatus}</Badge>
        </div>
      )}
      {shard && shard.region && (
        <div className="flex items-baseline gap-1">
          <p className="text-heading-lg text-primary">Region:</p>
          <p className="text-body">
            {shard.region
              ? `${code} - ${country} (${name})`
              : 'Router region not found'}
          </p>
        </div>
      )}
      {shard && shard.tier && (
        <div className="flex items-baseline gap-1">
          <p className="text-heading-lg">Tier: </p>
          <p className="text-body">{capitalize(router?.shard?.tier)}</p>
        </div>
      )}
    </div>
  );
};
